import { Box, IconButton, Stack, useTheme } from "@mui/joy";
import React from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { ListSummary } from "./ListSummary";
import { EditList } from "./EditList";
import { ViewList } from "./ViewList";
import { PlaceDetail } from "./PlaceDetail";
import { MapState } from "@/hooks/useMapState";

export const PLACES_AUTOCOMPLETE_WIDTH = 300;
const ICON_BUTTON_SIZE = 36;

const ListSummaryView = "list-summary";
const EditListView = "edit-list";
const ViewListView = "view-list";
const EditPlaceView = "edit-place";
const AddPlaceView = "add-place";
const ViewPlaceView = "view-place";
type PanelView =
  | { type: "list-summary" }
  | { type: "edit-list"; listId: string }
  | { type: "view-list"; listId: string }
  | { type: "edit-place"; listPlaceId: string; listId: string }
  | { type: "add-place"; googlePlaceId: string }
  | { type: "view-place"; listPlaceId: string; listId: string };

type MapToolbarPanelProps = {
  onClose: () => void;
  mapState: MapState;
  googlePlaceIdFromMap?: string | null;
  isMobile?: boolean;
  onExpandRequest?: () => void;
  initialListId?: string | null;
  initialListMode?: "edit" | "view" | null;
};

export const MapToolbarPanel = ({
  onClose,
  mapState,
  googlePlaceIdFromMap,
  isMobile = false,
  onExpandRequest,
  initialListId,
  initialListMode,
}: MapToolbarPanelProps) => {
  const theme = useTheme();

  // Initialize view based on whether we're coming from a map click or URL params
  const getInitialView = (): PanelView => {
    if (initialListId) {
      return {
        type: initialListMode === "view" ? ViewListView : EditListView,
        listId: initialListId,
      };
    }
    if (googlePlaceIdFromMap) {
      return { type: AddPlaceView, googlePlaceId: googlePlaceIdFromMap };
    }
    return { type: ListSummaryView };
  };

  const [currentView, setCurrentView] =
    React.useState<PanelView>(getInitialView);
  const [prevGooglePlaceId, setPrevGooglePlaceId] = React.useState<
    string | null | undefined
  >(googlePlaceIdFromMap);

  // Update view when googlePlaceIdFromMap changes (during render)
  if (googlePlaceIdFromMap && prevGooglePlaceId !== googlePlaceIdFromMap) {
    setPrevGooglePlaceId(googlePlaceIdFromMap);
    // Only switch to add-place mode if we're not already viewing a place
    if (currentView.type !== EditPlaceView) {
      setCurrentView({
        type: AddPlaceView,
        googlePlaceId: googlePlaceIdFromMap,
      });
      onExpandRequest?.();
    }
  }

  const handleBack = () => {
    switch (currentView.type) {
      case EditPlaceView:
        // Go back to edit-list with the correct listId
        setCurrentView({ type: EditListView, listId: currentView.listId });
        break;
      case EditListView:
        setCurrentView({ type: ListSummaryView });
        break;
      case ViewListView:
        setCurrentView({ type: ListSummaryView });
        break;
      case ViewPlaceView:
        // Go back to view-list with the correct listId
        setCurrentView({ type: ViewListView, listId: currentView.listId });
        break;
      case AddPlaceView:
        // Clear the map place and go to list-summary
        mapState.setMapPlaceId(null);
        setCurrentView({ type: ListSummaryView });
        break;
      default:
        break;
    }
  };

  // Helper to check if we should show back button
  const showBackButton = currentView.type !== ListSummaryView;

  const panelWidth = `calc(${PLACES_AUTOCOMPLETE_WIDTH}px + ${ICON_BUTTON_SIZE}px + ${theme.spacing(6)})`;

  const onPlaceClick = (placeId: string, listId: string) => {
    setCurrentView({ type: EditPlaceView, listPlaceId: placeId, listId });
    onExpandRequest?.();
  };

  // Track when we should show the place detail panel
  const showPlaceDetail =
    currentView.type === EditPlaceView ||
    currentView.type === AddPlaceView ||
    currentView.type === ViewPlaceView;

  // Helper function to handle saving a new place (closes add-place mode)
  const handleAddPlaceComplete = () => {
    mapState.setMapPlaceId(null);
    setCurrentView({ type: ListSummaryView });
  };

  // --- Mobile: single-view sequential navigation ---
  if (isMobile) {
    return (
      <Box
        data-testid="mobile-toolbar-panel"
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {/* Header with back/close button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            px: 1,
            pt: 1,
            flexShrink: 0,
          }}
        >
          {showBackButton ? (
            <IconButton
              aria-label="Back"
              size="md"
              sx={{ "--IconButton-size": `${ICON_BUTTON_SIZE}px` }}
              onClick={handleBack}
              data-testid="mobile-panel-back"
            >
              <ArrowBackIcon />
            </IconButton>
          ) : (
            <IconButton
              aria-label="Close panel"
              size="md"
              sx={{ "--IconButton-size": `${ICON_BUTTON_SIZE}px` }}
              onClick={onClose}
              data-testid="mobile-panel-close"
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>

        {/* Single-view content — place detail replaces list view on mobile */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 1, pb: "72px" }}>
          {showPlaceDetail ? (
            <>
              {currentView.type === EditPlaceView && (
                <PlaceDetail
                  mode="view"
                  listPlaceId={currentView.listPlaceId}
                  mapState={mapState}
                />
              )}
              {currentView.type === AddPlaceView && (
                <PlaceDetail
                  mode="add"
                  googlePlaceId={currentView.googlePlaceId}
                  mapState={mapState}
                  onSaveComplete={handleAddPlaceComplete}
                />
              )}
              {currentView.type === ViewPlaceView && (
                <PlaceDetail
                  mode="view"
                  listPlaceId={currentView.listPlaceId}
                  listId={currentView.listId}
                  mapState={mapState}
                />
              )}
            </>
          ) : (
            <>
              {currentView.type === ListSummaryView && (
                <ListSummary
                  onListClick={(listId, isSavedList) =>
                    setCurrentView({
                      type: isSavedList ? ViewListView : EditListView,
                      listId,
                    })
                  }
                />
              )}
              {currentView.type === ViewListView && (
                <ViewList
                  listId={currentView.listId}
                  onUnsave={() => setCurrentView({ type: ListSummaryView })}
                  onPlaceClick={(placeId) => {
                    setCurrentView({
                      type: ViewPlaceView,
                      listPlaceId: placeId,
                      listId: currentView.listId,
                    });
                    onExpandRequest?.();
                  }}
                />
              )}
              {currentView.type === EditListView && (
                <EditList
                  listId={currentView.listId}
                  onPlaceClick={(placeId) =>
                    onPlaceClick(placeId, currentView.listId)
                  }
                  onDelete={() => setCurrentView({ type: ListSummaryView })}
                />
              )}
            </>
          )}
        </Box>
      </Box>
    );
  }

  // --- Desktop: existing dual-panel layout ---
  return (
    <Stack direction="row" height="100vh">
      {/* List Summary or Edit List Panel */}
      <Box
        width={panelWidth}
        height="100vh"
        data-testid="list-summary-panel"
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: theme.vars.palette.background.body,
          position: "relative",
        })}
      >
        {showBackButton ? (
          <IconButton
            aria-label="Back"
            size="md"
            sx={{
              "--IconButton-size": `${ICON_BUTTON_SIZE}px`,
              position: "absolute",
              top: theme.spacing(2),
              right: theme.spacing(2),
              zIndex: 10,
            }}
            onClick={handleBack}
          >
            <ArrowBackIcon />
          </IconButton>
        ) : (
          <IconButton
            aria-label="Close panel"
            size="md"
            sx={{
              "--IconButton-size": `${ICON_BUTTON_SIZE}px`,
              position: "absolute",
              top: theme.spacing(2),
              right: theme.spacing(2),
              zIndex: 10,
            }}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        )}

        <Box
          paddingTop={theme.spacing(8)}
          flexGrow={1}
          sx={{ overflowY: "auto" }}
        >
          {currentView.type === ListSummaryView && (
            <ListSummary
              onListClick={(listId, isSavedList) =>
                setCurrentView({
                  type: isSavedList ? ViewListView : EditListView,
                  listId,
                })
              }
            />
          )}
          {(currentView.type === ViewListView ||
            currentView.type === ViewPlaceView) && (
            <ViewList
              listId={currentView.listId}
              onUnsave={() => setCurrentView({ type: ListSummaryView })}
              onPlaceClick={(placeId) =>
                setCurrentView({
                  type: ViewPlaceView,
                  listPlaceId: placeId,
                  listId: currentView.listId,
                })
              }
            />
          )}
          {(currentView.type === EditListView ||
            currentView.type === EditPlaceView) && (
            <EditList
              listId={currentView.listId}
              onPlaceClick={(placeId) =>
                onPlaceClick(placeId, currentView.listId)
              }
              onDelete={() => setCurrentView({ type: ListSummaryView })}
            />
          )}
        </Box>
      </Box>

      {/* Place Detail Panel - Slides in from the right */}
      {showPlaceDetail && (
        <Box
          width={panelWidth}
          height="100vh"
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            backgroundColor: theme.vars.palette.background.body,
            borderLeft: `1px solid ${theme.vars.palette.divider}`,
            animation: "slideIn 0.3s ease-out",
            "@keyframes slideIn": {
              from: {
                transform: "translateX(100%)",
                opacity: 0,
              },
              to: {
                transform: "translateX(0)",
                opacity: 1,
              },
            },
          })}
        >
          <Box
            paddingTop={theme.spacing(8)}
            flexGrow={1}
            sx={{ overflowY: "auto" }}
          >
            {currentView.type === EditPlaceView && (
              <PlaceDetail
                mode="view"
                listPlaceId={currentView.listPlaceId}
                mapState={mapState}
              />
            )}
            {currentView.type === AddPlaceView && (
              <PlaceDetail
                mode="add"
                googlePlaceId={currentView.googlePlaceId}
                mapState={mapState}
                onSaveComplete={handleAddPlaceComplete}
              />
            )}
            {currentView.type === ViewPlaceView && (
              <PlaceDetail
                mode="view"
                listPlaceId={currentView.listPlaceId}
                listId={currentView.listId}
                mapState={mapState}
              />
            )}
          </Box>
        </Box>
      )}
    </Stack>
  );
};
